import { media } from '@goalpost/content';
import { Link } from '../components/Link.tsx';
import { useTheme } from '../hooks/useTheme.tsx';
import { MediaVideo } from '../components/MediaVideo.tsx';
import { MarketingFooter } from '../components/marketing/MarketingFooter.tsx';
import { useDocumentTitle } from '../hooks/useDocumentTitle.ts';
import {
  AGENT_DEMO_VIDEO,
  FLOW_SHOT_SIZES,
  FLOW_SHOT_WIDTHS,
  shotSrcset,
} from '../marketing-shots.ts';
import { useT } from '../i18n.ts';
import '../styles/marketing.css';
import './Home.css';

// The feature list's entries, in page order; each resolves a title and body
// under `home.features.<key>` in the content catalog.
const FEATURES = ['nested', 'labels', 'search', 'notifications', 'approvals'] as const;

export function Home() {
  const t = useT();
  const { theme } = useTheme();
  useDocumentTitle(t('home.document-title'));

  // Theme-matched same-origin captures. `graphite` (dark) → the dark capture;
  // everything else (porcelain) → light. Kept in sync with the preload emitted by
  // the inline theme bootstrap in index.html, which resolves the same theme
  // pre-paint.
  const shot = theme === 'graphite' ? 'dark' : 'light';

  // The media between the boxes is the agent demo clip (AGENT_DEMO_VIDEO)
  // once the media manifest carries it, and the document screenshot until
  // then, so it never renders an empty figure on a checkout that hasn't
  // synced the asset.
  const hasAgentDemo = media(`${AGENT_DEMO_VIDEO}-light-cc.mp4`) !== null;

  return (
    <div className="mkt-page" data-gp-conversion-impression="home_page_view">
      {/*
        Hero ─ a compact brand lockup. The logo mark and the "Goalpost" wordmark
        are brand chrome; the tagline is the `h1`, so the page's heading carries
        what the tool is rather than the product's name.
      */}
      <section className="mkt-hero mkt-hero-lead mkt-hero-brand" data-gp-name="hero" data-gp-observe-impression="true">
        <div className="mkt-container">
          <div className="mkt-lockup">
            {/* Decorative: the adjacent wordmark already names the brand. */}
            <img
              className="mkt-lockup-mark"
              src="/goalpost_logo.svg"
              alt=""
              width={208}
              height={208}
            />
            <div className="mkt-lockup-text">
              <span className="mkt-lockup-wordmark">Goalpost</span>
              <h1 className="mkt-h1">{t('home.hero.title')}</h1>
              <div className="mkt-cta-row">
                <Link
                  to="/register"
                  className="mkt-btn primary lg"
                  data-gp-name="signup-cta"
                  data-gp-observe-interact="true"
                  data-gp-conversion-interact="signup_cta"
                >
                  {t('marketing.cta.signup')}
                </Link>
                <Link
                  to="/dashboard"
                  className="mkt-btn ghost lg"
                  data-gp-name="login-cta"
                  data-gp-observe-interact="true"
                >
                  {t('marketing.cta.login')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/*
        The first box: what Goalpost is, in plain terms. The prose describes
        the thing — a document for people and machines to share notes, context,
        reference materials, requirements, and decisions — and the links lead into the
        reference docs for anyone who wants more.
      */}
      <section className="mkt-section mkt-about-section" data-gp-name="about" data-gp-observe-impression="true">
        <div className="mkt-container">
          <div className="mkt-about">
            <h2 className="mkt-h2">{t('home.about.heading')}</h2>
            <div className="mkt-about-row">
              <div className="mkt-split-body">
                <p>{t('home.about.body-1')}</p>
                <p>{t('home.about.body-2')}</p>
                <p>{t('home.about.body-3')}</p>
              </div>
              {/* The reading-on links, beside the prose rather than beneath it. */}
                <ul className="mkt-about-links">
                  <li>
                    <Link
                      to="/docs"
                      className="mkt-pillar-cta"
                      data-gp-name="learn-more"
                      data-gp-observe-interact="true"
                    >
                      {t('home.about.link.getting-started')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/features/documents"
                      className="mkt-pillar-cta"
                      data-gp-name="learn-more"
                      data-gp-observe-interact="true"
                    >
                      {t('home.about.link.documents')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/features/ai"
                      className="mkt-pillar-cta"
                      data-gp-name="learn-more"
                      data-gp-observe-interact="true"
                    >
                      {t('home.about.link.ai')}
                    </Link>
                  </li>
                </ul>
            </div>
          </div>
        </div>
      </section>

      {/*
        The tool itself, between the two boxes and unframed, set to the width of
        the text inside them: an agent writing the document, once the clip is
        synced — the plainest evidence for "people and machines" the page has.
        Click-to-play over its poster frame, so nothing autoplays.

        Otherwise the live document mid-edit (the `edit-document` image target —
        marketing/scripts/targets.ts). Served same-origin from
        www/public/marketing/ rather than resolved from the media manifest: it is
        the first image on the page and the likely LCP element on a tall
        viewport, and only a build-stable path can be named by the pre-paint
        preload in index.html. Eager and high-priority to match that preload.
      */}
      <section className="mkt-section mkt-media-section" data-gp-name="agent-demo" data-gp-observe-impression="true">
        <div className="mkt-container">
          {hasAgentDemo ? (
            <MediaVideo
              base={AGENT_DEMO_VIDEO}
              label={t('home.about.video-label')}
              className="mkt-home-media"
            />
          ) : (
            <figure className="mkt-home-media">
              <img
                src={`/marketing/edit-document-${shot}.webp`}
                srcSet={shotSrcset('edit-document', shot, FLOW_SHOT_WIDTHS)}
                sizes={FLOW_SHOT_SIZES}
                alt={t('home.about.image-alt')}
                width={1600}
                height={1000}
                fetchPriority="high"
                decoding="async"
              />
            </figure>
          )}
        </div>
      </section>

      {/* The second box: the feature list, as plain facts about the tool. */}
      <section className="mkt-section mkt-features-section" data-gp-name="features" data-gp-observe-impression="true">
        <div className="mkt-container">
          <div className="mkt-about">
            <h2 className="mkt-h2">{t('home.features.heading')}</h2>
            <ul className="mkt-feature-list">
              {FEATURES.map((feature) => (
                <li key={feature}>
                  <strong>{t(`home.features.${feature}.title`)}</strong>
                  <span>{t(`home.features.${feature}.body`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* The closing call to action: one centered Sign up button, nothing
          else. */}
      <section className="mkt-section mkt-home-cta" data-gp-name="closing-cta" data-gp-observe-impression="true">
        <div className="mkt-container">
          <Link
            to="/register"
            className="mkt-btn primary lg"
            data-gp-name="signup-cta"
            data-gp-observe-interact="true"
            data-gp-conversion-interact="signup_cta"
          >
            {t('marketing.cta.signup')}
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
